import { Check, X } from "lucide-react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";

export type DomainStatus = "complete" | "running" | "queued" | "error";

export interface DomainNode {
  id: string;
  /** Já traduzido por quem chama. */
  label: string;
  status: DomainStatus;
}

// Mesmo vocabulário do PipelineProgress: o nome acessível de cada domínio já é lido assim.
const STATUS_TEXT: Record<DomainStatus, string> = {
  complete: "Completed",
  error: "Errored",
  running: "Now running",
  queued: "Pending",
};

/** Quantos domínios, contados do primeiro, já estão prontos em sequência. */
function leadingComplete(nodes: DomainNode[]): number {
  let count = 0;
  for (const node of nodes) {
    if (node.status !== "complete") break;
    count += 1;
  }
  return count;
}

/** Faixa informativa dos domínios da extração (sem ação: não são abas). */
export function ExtractionDomains({ nodes }: { nodes: DomainNode[] }) {
  const { t } = useTranslation();
  const done = nodes.filter((node) => node.status === "complete").length;
  const errored = nodes.filter((node) => node.status === "error").length;
  const pending = nodes.length - done - errored;
  // A linha liga o centro do primeiro ao do último nó; preenche até o último pronto.
  const fill = nodes.length > 1 ? (Math.max(leadingComplete(nodes) - 1, 0) / (nodes.length - 1)) * 100 : 0;
  const inset = `${100 / nodes.length / 2}%`;

  return (
    <section className="flex flex-col gap-5 rounded-lg border border-[var(--exed-card-border)] bg-[var(--exed-card-bg)] px-6 pb-6 pt-5">
      <div className="flex flex-wrap justify-between gap-4 font-mono text-[11px] font-medium uppercase tracking-[0.12em]">
        <span className="text-[#8a6100]">{t("Extraction · 3 domains")}</span>
        <span className="text-[var(--exed-ink-500)]">
          {t("{{done}} done · {{pending}} pending", { done, pending })}
          {errored > 0 && <> · <span className="text-[#a3262c]">{t("{{count}} errored", { count: errored })}</span></>}
        </span>
      </div>
      <ol className="relative m-0 grid list-none p-0" style={{ gridTemplateColumns: `repeat(${nodes.length}, minmax(0, 1fr))` }}>
        <span
          aria-hidden="true"
          className="absolute top-[19px] h-0.5 bg-[var(--exed-card-border)]"
          style={{ left: inset, right: inset }}
        >
          <span
            data-testid="domain-line-fill"
            className="block h-full bg-[var(--exed-success)] transition-[width] duration-500 motion-reduce:transition-none"
            style={{ width: `${fill}%` }}
          />
        </span>
        {nodes.map((node, index) => {
          const num = String(index + 1).padStart(2, "0");
          return (
            <li
              key={node.id}
              aria-label={`${node.label}: ${t(STATUS_TEXT[node.status])}`}
              className="relative flex flex-col items-center gap-2 text-center"
            >
              <span
                data-testid={node.status === "running" ? "domain-running" : undefined}
                className={cn(
                  "flex h-10 w-10 items-center justify-center rounded-full font-mono text-xs font-semibold shadow-[0_0_0_4px_#fff]",
                  node.status === "complete" && "bg-[var(--exed-success)] text-white",
                  node.status === "error" && "bg-[var(--exed-danger)] text-white",
                  node.status === "running" &&
                    "animate-pulse border-[3px] border-[var(--exed-gold)] bg-white text-[#8a6100] motion-reduce:animate-none",
                  node.status === "queued" && "border-[3px] border-[var(--exed-ink-100)] bg-white text-[var(--exed-ink-300)]",
                )}
              >
                {node.status === "complete" ? (
                  <Check data-testid="domain-check" aria-hidden="true" className="h-[18px] w-[18px]" />
                ) : node.status === "error" ? (
                  <X aria-hidden="true" className="h-[18px] w-[18px]" />
                ) : (
                  num
                )}
              </span>
              <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-[var(--exed-ink-500)]">
                {t("Node {{num}}", { num })}
              </span>
              <span className="break-words text-sm font-semibold text-[var(--exed-ink-900)]">{node.label}</span>
            </li>
          );
        })}
      </ol>
    </section>
  );
}
